import filter from 'lodash/filter';
import uniqBy from 'lodash/uniqBy';
import clone from 'lodash/clone';
import {
  APP_ERROR,
  APP_LOADING,
  CLOSE_INDIVIDUAL_STUDENT_GRADEBOOK,
  GET_GRADEBOOK_AVERAGES_SUCCESS,
  GET_GRADEBOOK_ERROR,
  GET_GRADEBOOK_NEXT_PAGE_SUCCESS,
  GET_GRADEBOOK_REQUEST,
  GET_GRADEBOOK_SETTINGS_SUCCESS,
  GET_GRADEBOOK_SUCCESS,
  GET_INDIVIDUAL_STUDENT_GRADEBOOK_DATA,
  GET_INSTRUCTOR_GRADEBOOK_ERROR,
  GET_INSTRUCTOR_GRADEBOOK_REQUEST,
  GET_INSTRUCTOR_GRADEBOOK_SUCCESS,
  GRADEBOOK_SETTINGS_UPDATING,
  INDIVIDUAL_STUDENT_GRADEBOOK_LOADING,
  OPEN_INDIVIDUAL_STUDENT_GRADEBOOK,
  REMOVE_ASSIGNMENT,
  RESET_GRADEBOOK,
  SELECT_INDIVIDUAL_STUDENT_GRADEBOOK,
  SET_GRADEBOOK_EXCEPTION_GROUPS,
  SET_GRADEBOOK_FILTER_TYPES,
  SET_GRADEBOOK_SEARCH_PHRASE,
  SET_GRADEBOOK_V2_ACTIVE,
  SET_INDIVIDUAL_STUDENT_GRADEBOOK,
  TOGGLE_CATEGORY_EXPAND_COLLAPSE,
  TOGGLE_PERFORMANCE_OVERVIEW_COLLAPSE,
  TOGGLE_INDIVIDUAL_STUDENT_GRADEBOOK,
  TOGGLE_SHOW_FILTER_OPTIONS_GRADEBOOK,
  UPDATE_GRADEBOOK,
  UPDATE_GRADEBOOK_ALERT,
  UPDATE_GRADEBOOK_INSTRUCTOR,
  UPDATE_GRADEBOOK_STUDENT,
  UPDATE_INDIVIDUAL_STUDENT_GRADEBOOK,
  UPDATE_SEARCH_TYPE,
  UPDATE_SEARCH_TYPE_MENU,
} from '../redux/constants';

import { GRADEBOOK_SORT } from '../utils/constants';
import { updateSearchParamsInBrowser } from '../utils/util';
import { applyResourceTypeFiltersToGradebookState } from '../utils/gradebookFilterState';

export const initialState = {
  loading: true,
  isSettingsModalMounted: true,
  isGradebookV2Active: false,
  isSettingsUpdating: false,
  anchorEl: null,
  pages: 1,
  error: null,
  searchPhrase: '',
  searchType: null,
  searchTypeMenuOpen: false,
  searchTypeMenuAnchorEl: null,
  searchDateFrom: null,
  searchDateTo: null,
  students: [],
  currentStudent: {
    catToggle: [],
    mobileExpandedCategories: [],
  },
  settings: [],
  categories: [],
  categoryWeights: [],
  averages: [],
  collapsedCategoryIds: [],
  collapsedPerformanceOverview: false,
  hasGradebookFetched: false,
  isGradebookFetching: false,
  isGradebookReFetching: false,
  iClickerStudentRosterMap: {},
  showFilterOptions: false,
  exceptionGroups: [],
  filterTypes: [],
  unfilteredAssignments: [],
  filterValue: '',
  visibleFilterSection: 'EXCEPTION_GROUPS_GB_FILTER_SECTION_ID',
  showSettings: false,
  showIndividualStudentGradebook: false,
  individualStudentGradebookIsLoading: false,
  individualStudentGradebookData: {
    currentStudent: null,
    showPercent: false,
    timeOnTaskMap: {},
    studentGradebook: {
      categoriesData: [],
      course: {},
      studentPerformanceData: [],
    },
  },
  isGradeSyncInProgress: false,
  showGradePercentage: true,
  open: false,
  reefMenuOpen: false,
  selectedStudent: null,
  showGradeOverrideDialog: false,
  showGradebookExportDialog: false,
  showGradebookCommentDialog: false,
  showLinkReefDialog: false,
  showUnlinkReefDialog: false,
  sortColumn: GRADEBOOK_SORT.NAME_SORT_COLUMN,
  sortColumnHistory: [GRADEBOOK_SORT.NAME_SORT_COLUMN],
  gradeOverrideData: {},
  gradeCommentData: {},
  alert: {},
  table: {
    students: [],
    sortedCol: null,
    sortDirection: null,
    containerWidth: 0,
    fixedLeftWidth: 0,
  },
  instructor: {
    gradebookTableHeight: 0,
    gradebook: {
      scrollToRow: -1,
      scrollToColumn: -1,
      hoveredColumnIndex: null,
      hoveredRowIndex: null,
    },
  },
};

function gradebook(state = initialState, action) {
  switch (action.type) {
    case APP_LOADING: {
      return Object.assign({}, state, {
        loading: action.payload,
      });
    }

    case APP_ERROR: {
      return Object.assign({}, state, {
        error: action.payload,
        loading: false,
      });
    }

    case RESET_GRADEBOOK: {
      return initialState;
    }

    case GRADEBOOK_SETTINGS_UPDATING: {
      return {
        ...state,
        isSettingsUpdating: action.payload,
      };
    }

    case GET_GRADEBOOK_SETTINGS_SUCCESS:
    case TOGGLE_SHOW_FILTER_OPTIONS_GRADEBOOK: {
      return {
        ...state,
        ...action.payload,
      };
    }

    case GET_GRADEBOOK_AVERAGES_SUCCESS: {
      const { averages, ...otherState } = state;
      const { averages: payloadAverages } = action.payload;
      const mergedAverages = uniqBy(
        [...payloadAverages, ...averages],
        'assignmentId',
      );
      return {
        ...otherState,
        averages: mergedAverages,
      };
    }

    case GET_GRADEBOOK_REQUEST: {
      return {
        ...state,
        isGradebookFetching: !state.hasGradebookFetched,
        isGradebookReFetching: state.hasGradebookFetched,
        ...action.payload,
      };
    }

    case GET_GRADEBOOK_ERROR: {
      return {
        ...state,
        isGradebookFetching: false,
        isGradebookReFetching: false,
      };
    }

    case GET_GRADEBOOK_SUCCESS: {
      return {
        ...state,
        ...action.payload,
        hasGradebookFetched: true,
        isGradebookFetching: false,
        isGradebookReFetching: false,
      };
    }

    // New instructor gradebook endpoint actions
    case GET_INSTRUCTOR_GRADEBOOK_REQUEST: {
      return {
        ...state,
        isGradebookFetching: !state.hasGradebookFetched,
        isGradebookReFetching: state.hasGradebookFetched,
        ...action.payload,
      };
    }

    case GET_INSTRUCTOR_GRADEBOOK_ERROR: {
      return {
        ...state,
        isGradebookFetching: false,
        isGradebookReFetching: false,
      };
    }

    case GET_INSTRUCTOR_GRADEBOOK_SUCCESS: {
      return {
        ...state,
        ...action.payload,
        hasGradebookFetched: true,
        isGradebookFetching: false,
        isGradebookReFetching: false,
      };
    }

    case SET_GRADEBOOK_EXCEPTION_GROUPS:
    case SET_GRADEBOOK_SEARCH_PHRASE: {
      return {
        ...state,
        ...action.payload,
        isGradebookReFetching: true,
      };
    }

    case SET_GRADEBOOK_FILTER_TYPES: {
      const nextState = {
        ...state,
        ...action.payload,
        isGradebookReFetching: true,
      };

      return applyResourceTypeFiltersToGradebookState(nextState);
    }

    case SET_GRADEBOOK_V2_ACTIVE: {
      return {
        ...state,
        isGradebookV2Active: action.payload,
      };
    }

    case GET_GRADEBOOK_NEXT_PAGE_SUCCESS: {
      const { students, ...rest } = action.payload;
      return {
        ...state,
        ...rest,
        students: [...state.students, ...students],
        isGradebookFetching: false,
        isGradebookReFetching: false,
      };
    }

    case SELECT_INDIVIDUAL_STUDENT_GRADEBOOK: {
      const { studentId } = action.payload;
      const { students } = state;
      const selectedStudent = students.filter(
        (s) => s.studentId === studentId,
      )[0];
      return {
        ...state,
        selectedStudent: selectedStudent && {
          ...selectedStudent,
        },
      };
    }

    case TOGGLE_INDIVIDUAL_STUDENT_GRADEBOOK: {
      const { studentId } = action.payload;
      const { students, showIndividualStudentGradebook } = state;
      const selectedStudent = !showIndividualStudentGradebook
        ? students.filter((s) => s.studentId === studentId)[0]
        : null;

      return {
        ...state,
        selectedStudent: selectedStudent && {
          ...selectedStudent,
        },
        showIndividualStudentGradebook: !showIndividualStudentGradebook,
      };
    }

    case UPDATE_INDIVIDUAL_STUDENT_GRADEBOOK: {
      return {
        ...state,
        individualStudentGradebookData: {
          ...state.individualStudentGradebookData,
          ...action.payload,
        },
      };
    }

    case INDIVIDUAL_STUDENT_GRADEBOOK_LOADING: {
      return {
        ...state,
        individualStudentGradebookIsLoading: action.payload,
      };
    }

    case SET_INDIVIDUAL_STUDENT_GRADEBOOK: {
      const { currentStudent } = action.payload;
      return {
        ...state,
        individualStudentGradebookData: {
          ...state.individualStudentGradebookData,
          currentStudent,
        },
      };
    }

    case GET_INDIVIDUAL_STUDENT_GRADEBOOK_DATA: {
      const { timeOnTaskMap, studentGradebook } = action.payload;

      return {
        ...state,
        individualStudentGradebookData: {
          ...state.individualStudentGradebookData,
          timeOnTaskMap,
          studentGradebook,
        },
      };
    }

    case OPEN_INDIVIDUAL_STUDENT_GRADEBOOK: {
      const studentId = action.payload?.studentId;

      const selectedStudent = studentId
        ? state?.roster?.filter((s) => s.studentId === studentId)[0]
        : null;

      const showPercent = state.settings.find(
        (setting) => setting.key === 'showPercent',
      )?.value;

      const isShowPercent = showPercent !== 'false' || showPercent === false;
      const searchParams = new URLSearchParams(window.location.search);

      if (!searchParams.has('showPercent')) {
        searchParams.set('showPercent', isShowPercent);
      }

      searchParams.set('studentId', studentId);
      updateSearchParamsInBrowser(searchParams);

      return {
        ...state,
        individualStudentGradebookData: {
          ...state.individualStudentGradebookData,
          currentStudent: selectedStudent,
        },
      };
    }

    case CLOSE_INDIVIDUAL_STUDENT_GRADEBOOK: {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('studentId');
      searchParams.delete('showPercent');
      updateSearchParamsInBrowser(searchParams);

      return {
        ...state,
        individualStudentGradebookData:
          initialState.individualStudentGradebookData,
      };
    }

    case TOGGLE_CATEGORY_EXPAND_COLLAPSE: {
      const { categoryId } = action.payload;
      const { collapsedCategoryIds } = state;
      const collapsedCatId = collapsedCategoryIds.find(
        (id) => id === categoryId,
      );
      const index = collapsedCategoryIds.indexOf(categoryId);

      if (collapsedCatId == null) {
        return {
          ...state,
          collapsedCategoryIds: [...collapsedCategoryIds, categoryId],
        };
      }

      return {
        ...state,
        collapsedCategoryIds: [
          ...collapsedCategoryIds.slice(0, index),
          ...collapsedCategoryIds.slice(index + 1),
        ],
      };
    }

    case TOGGLE_PERFORMANCE_OVERVIEW_COLLAPSE: {
      return {
        ...state,
        collapsedPerformanceOverview: !state.collapsedPerformanceOverview,
      };
    }

    case UPDATE_GRADEBOOK: {
      return {
        ...state,
        ...action.gradebook,
      };
    }
    case UPDATE_GRADEBOOK_STUDENT: {
      return {
        ...state,
        currentStudent: Object.assign(
          state.currentStudent,
          action.currentStudent,
        ),
      };
    }
    case UPDATE_GRADEBOOK_INSTRUCTOR: {
      return {
        ...state,
        instructor: Object.assign(state.instructor, action.instructor),
      };
    }
    case UPDATE_GRADEBOOK_ALERT: {
      return {
        ...state,
        alert: Object.assign({}, action.alert),
      };
    }
    case UPDATE_SEARCH_TYPE: {
      return {
        ...state,
        searchType: action.searchType.slice(),
      };
    }
    case UPDATE_SEARCH_TYPE_MENU: {
      return {
        ...state,
        searchTypeMenuOpen: !state.searchTypeMenuOpen,
        searchTypeMenuAnchorEl: action.anchorEl,
      };
    }

    case REMOVE_ASSIGNMENT: {
      const assignment = action.payload.assignment;
      const myState = clone(state);
      myState.assignments = filter(
        myState.assignments,
        ({ id }) => id !== assignment.id,
      );
      myState.averages = filter(
        myState.averages,
        ({ assignmentId }) => assignmentId !== assignment.id,
      );
      myState.students.forEach((student) => {
        student.assignments = filter(
          student.assignments,
          ({ id }) => id !== assignment.id,
        );
        student.unfilteredAssignments = filter(
          student.unfilteredAssignments,
          ({ id }) => id !== assignment.id,
        );
      });
      return myState;
    }

    default: {
      return state;
    }
  }
}

gradebook.constants = {
  initialState,
};

export default gradebook;
